/**
 * ListingsClient - Orchestrator for listings browse page
 *
 * Diamond Architecture:
 * - Manages shared state between Map and Sheet
 * - Handles bounds changes → refetch via useInfiniteQuery
 * - Handles infinite scroll → fetchNextPage
 * - All data typed with proto-generated ListingCard
 *
 * TanStack Query:
 * - Bounds snapped to grid for cache efficiency
 * - Automatic stale query cancellation (no manual requestIdRef)
 * - Cached results when user pans back to previous areas
 *
 * Used by: /listings/page.tsx (Server Component)
 */
import { SelectedListingCard } from '@/shared/components/listings';
import { useTranslations } from '@/shared/i18n/useTranslations';
import type { ListingsFilterSet } from '@/shared/lib/listings/listings-filter-set';
import type { ListingCardData, MapPositionData } from '@/shared/lib/types/listing-card';
import type { TransactionModeValue } from '@letsgethome/lib/types/listings';
import { type Route } from '@letsgethome/routes';
import type { PageStanding } from '@letsgethome/types/paginated';
import { useIsMobile } from '@letsgethome/ui/hooks/use-mobile';
import { cn } from '@letsgethome/ui/lib/utils';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';

import type { ListingsSurface } from './listings-surface';
import { ListingsMap } from './ListingsMap';
import { ListingsPager } from './ListingsPager';
import { ListingsSheet } from './ListingsSheet';
import { ListingsTopBar } from './ListingsTopBar';
import { MAP_PREVIEW_TESTID } from './map-popover';
import { useListingsMapState } from './use-listings-map-state';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

export interface ListingsClientProps {
  initialListings: ListingCardData[];
  initialMeta: {
    totalCount: number;
    page: number;
    perPage: number;
    hasMore: boolean;
    /**
     * The served document's standing — REQUIRED, so a canvas cannot be built
     * without saying whether its page is past the last result (#6922). The
     * pager and the results pane each render the `pastTheEnd` arm.
     */
    standing: PageStanding;
  };
  initialMapPosition?: MapPositionData;
  mode: TransactionModeValue;
  /**
   * The URL's filter set, from the page's loader.
   *
   * REQUIRED — deliberately not `?`-optional even though it can be empty. All
   * three call sites (buy, rent, district facet) previously omitted it, which
   * is what made a map pan drop the user's filters and pinned the badge at
   * zero. A page that forgets it now fails to compile.
   */
  filters: ListingsFilterSet;
  /**
   * The href of page `n` of THIS surface — `useListingsPageHref(base)` with the
   * page's own base route (the hub, a district facet, a dwelling-type facet).
   *
   * REQUIRED, like `filters` above and for the same reason: every surface that
   * renders this sheet paged through a JS-only scroll listener and served NO
   * `<a href="…?page=2">`, so a crawler could reach the first page of cards and
   * nothing after it. A page that forgets its pager now fails to compile.
   */
  hrefForPage: (page: number) => Route;
  /**
   * WHERE this canvas is embedded — REQUIRED, like `filters` and `hrefForPage`
   * above and for the same reason: every consumer had the same unstated answer
   * until one of them did not.
   *
   * It decides the canvas's own HEIGHT, and a height is not something this
   * component can know. `h-full` is 100 % of the column, which is right when
   * `main` is bounded and this is its fill pane, and is what handed the district
   * facets a **9.25px** results pane at 393x852 when it was a flex item beside a
   * heading and an SEO prose block (`flex-shrink: 1` is the default). A surface
   * that scrolls the document has to be told a real number, and the page that
   * embeds it is the only thing that knows which of the two it is.
   */
  surface: ListingsSurface;
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

/** The map's pins while the pane holds no list — one module-level value, so the map's props stay stable. */
const NO_PINS: ListingCardData[] = [];

export function ListingsClient({
  initialListings,
  initialMeta,
  initialMapPosition,
  mode,
  filters,
  hrefForPage,
  surface,
}: ListingsClientProps) {
  const t = useTranslations('common');
  const isMobile = useIsMobile();

  const {
    mapPosition,
    selectedId,
    markerPreview,
    results,
    isRefreshing,
    isFetchingNextPage,
    hasNextPage,
    handleMarkerClick,
    handleDismissPreview,
    handleCardHighlight,
    handleInteractionStart,
    handleBoundsChange,
    handleLoadMore,
    sheetPosition,
    onSheetEvent,
  } = useListingsMapState({
    initialListings,
    initialMeta,
    initialMapPosition,
    mode,
    filters,
  });

  const infoWindowContent = markerPreview ? (
    <SelectedListingCard
      listing={markerPreview}
      transactionType={mode}
      onDismiss={handleDismissPreview}
    />
  ) : null;

  return (
    // ★ THE HEIGHT IS THE `surface` PROP'S, and `relative` is not optional.
    //
    // `h-full` = 100 % of the column. That is the right answer when the SHELL
    // has bounded the column — `chrome: canvas` makes `main` `h-svh` (and
    // `100svh - navbar` at md+) with `overflow-hidden` — and this is its fill
    // pane. It is the WRONG answer, and silently so, for a page that renders
    // flow content beside the canvas: `h-full` on a flex item with the default
    // `flex-shrink: 1` means "100 %, or whatever my siblings leave", and what
    // the district facets left was 9.25px at 393x852. An `inline` surface is
    // therefore given a real height, once, here.
    //
    // `relative` is what makes that height MEAN anything. The map pane below is
    // `absolute inset-0` at mobile widths; with no positioned ancestor it
    // resolved against the initial containing block, which happened to equal
    // `main` on a full-viewport canvas and would be the whole page on a
    // scrolling one. It is a no-op on the hub (the two boxes coincide) and the
    // difference between a map and a full-page overlay on a facet.
    //
    // `data-slot` so the e2e can measure this box without naming the classes
    // that produce its geometry.
    <div
      data-slot="listings-canvas"
      className={cn(
        'relative flex w-full flex-col overflow-hidden',
        surface === 'canvas'
          ? 'h-full'
          : // A block in a document: tall enough to be a map and short enough
            // that the prose under it is reachable by scrolling rather than by
            // faith. `svh` and not `dvh` for `PageShell`'s reason — the small
            // viewport is static, so the block does not resize as the URL bar
            // collapses.
            'h-[70svh] min-h-[26rem] rounded-xl border border-border md:h-[34rem]'
      )}
    >
      {/* NEITHER the filters NOR the search is this orchestrator's any more.
          The filters trigger, its badge and its modal are one
          `ListingsFiltersControl` mounted by the bar itself and by the desktop
          navbar, each deriving the badge from the URL — holding them here is
          what confined the filters to this `md:hidden` bar, since the navbar is
          `infra` and may not import this feature (obs. 2). The search form and
          its overlay are the shared `SearchForm`'s, which owns the `open`
          state it needs — this file held `isSearchOpen` and a
          `handleSearchClick` that shipped as a comment ("Will open search modal
          in a future PR"), and a control in one module with its behaviour in
          another is exactly how that no-op survived (obs. 6). Both cells are
          now one component with two placements, taking what they need rather
          than being handed it — including the active query, which the field
          reads off the URL through the listings-filter codec instead of taking
          `filters.query` from here. */}
      <ListingsTopBar surface={surface} />

      {/* THE PANES. One wrapper for both surfaces, so the two geometries are
          two class lists rather than two component trees. On a `canvas` it is
          the whole (bounded) column and the map is `absolute inset-0` over it
          below `md`, with the results a drawer on top; `inline` stacks them,
          half and half, because a viewport-FIXED drawer over a scrolling
          document would sit on the page's own prose forever. */}
      <div
        className={cn(
          'relative flex w-full min-h-0 flex-1',
          surface === 'inline' && 'flex-col md:flex-row'
        )}
      >
        <div
          className={
            surface === 'canvas'
              ? 'absolute inset-0 md:relative md:w-1/2'
              : 'relative h-1/2 w-full md:h-full md:w-1/2'
          }
        >
          <ListingsMap
            // Pins for the list the pane is showing — none while there is no
            // list (a first load, an error), never a list the key does not
            // describe.
            listings={results.kind === 'ready' ? results.listings : NO_PINS}
            isRent={mode === 'rent'}
            initialPosition={mapPosition}
            selectedId={selectedId}
            selectedListing={markerPreview}
            onMarkerClick={handleMarkerClick}
            onInteractionStart={handleInteractionStart}
            onBoundsChange={handleBoundsChange}
            className="h-full w-full"
            infoWindowContent={infoWindowContent}
            sheetOverlaysMap={surface === 'canvas'}
          />

          {isMobile && markerPreview && (
            // ⚠ THE ESCAPE THIS ROSTER WAS BUILT TO ABSORB (D16/D17). This was a
            // raw `z-40`: TIED with `MobileBottomNav` and one number below the
            // results sheet that slides over it, in the same feature as
            // `MobileSheet`, and reported CLEAN by a lint whose pin-detector
            // accepted only `fixed|sticky`. The pane above is `absolute` with no
            // z of its own, so it creates no stacking context and this card
            // really is compared against page chrome.
            //
            // ★ It is STILL one number below `resultsSheet`, deliberately
            // (obs. 5, cause 3). The pair is resolved by STATE, not z-order —
            // and by ONE piece of state, which is the round-2 fix: this card and
            // the drawer's `open` are both pure functions of `markerPreview`, so
            // the drawer is off-screen for exactly as long as this card renders,
            // in the same render pass. (It was two: a `previewOpened` /
            // `previewDismissed` pair drove a stored `hiddenForPreview`, which a
            // DESKTOP pin click could set for a drawer that was not mounted and
            // which a card hover could then strand.) Raising this row above the
            // sheet instead would have made the card visible while leaving the
            // drawer's disappearance uncaused and its return unguaranteed,
            // which is the observation.
            <div
              // The preview's own identity — the element a browser test names to
              // ask "is the drawer legitimately off screen?". See
              // `MAP_PREVIEW_TESTID`.
              data-testid={MAP_PREVIEW_TESTID}
              className={cn(
                'absolute bottom-4 left-4 right-4',
                Z_LAYERS.mapPreview.safeArea,
                Z_LAYERS.mapPreview.className
              )}
            >
              <SelectedListingCard
                listing={markerPreview}
                transactionType={mode}
                onDismiss={handleDismissPreview}
              />
            </div>
          )}
        </div>

        <ListingsSheet
          surface={surface}
          results={results}
          onCardHighlight={handleCardHighlight}
          onLoadMore={handleLoadMore}
          isLoadingMore={isFetchingNextPage}
          hasMore={hasNextPage}
          mode={mode}
          sheetPosition={sheetPosition}
          onSheetEvent={onSheetEvent}
          // The drawer's openness, as the FACT it is derived from — the same
          // value the preview card two blocks up is rendered from, so the two
          // cannot disagree. See `ListingsSheetProps.markerPreview`.
          markerPreview={markerPreview}
          // Derived from the SERVED document's meta, not the infinite query's
          // live `hasNextPage`: these anchors are the crawl contract for this
          // URL and must be identical before and after hydration.
          footer={
            <ListingsPager
              page={initialMeta.page}
              hasMore={initialMeta.hasMore}
              standing={initialMeta.standing}
              hrefForPage={hrefForPage}
            />
          }
        />
      </div>

      {/* The REFRESHING cue — a query fetch OR a search-only navigation of this
          page (a filter Apply), so the pane never shows a list that does not
          match the URL's badge without saying a new one is on its way
          (`useListingsMapState`'s `isRefreshing`). */}
      {isRefreshing && (
        <div
          role="status"
          className={cn(
            'absolute top-4 left-1/2 -translate-x-1/2',
            Z_LAYERS.transientStatus.className
          )}
        >
          <div className="bg-background rounded-full px-4 py-2 shadow-lg flex items-center gap-2">
            <div className="h-4 w-4 border-2 border-highlight-text border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-muted-foreground">{t('loading')}</span>
          </div>
        </div>
      )}
    </div>
  );
}
